/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { lazy, Suspense, useEffect, useMemo, useState } from "react";

// react-router components
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
// @mui material components
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";
import { ThemeProvider } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Configurator from "examples/Configurator";
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 React themes
import theme from "assets/theme";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";

// Theme with accent from settings (sidenav color → primary, borders)
import getThemeWithAccent from "assets/theme/themeWithAccent";

// RTL plugins
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import { getRoutesByRole } from "routes";
import {
  filterRoutesByWebPermissions,
  firstAccessibleHomePath,
} from "./utils/filterRoutesByWebPermissions";

// Material Dashboard 2 React contexts
import {
  setMiniSidenav,
  setOpenConfigurator,
  useMaterialUIController,
} from "context";

// Images
import brandDark from "assets/images/logo-ct-dark.png";
import brandWhite from "assets/images/logo-ct.png";
import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import ForgotPassword from "layouts/authentication/forgot-password";
import ForgotPasswordConfirm from "layouts/authentication/forgot-password/Confirm";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import {
  GetDashboardReportCall,
  GetMapReportCall,
} from "./redux/api/report.api";
import { persistor, store } from "./redux/store";
import PresentationMobileViewPage from "./prof/presentationTitles/PresentationMobileViewPage";
import PublicQuestionPage from "layouts/publicQuestion";
import LandingPage from "layouts/landing";
import DemoRequestPage from "layouts/demoRequestPage";
import i18n from "./i18n";
import { startImpersonation, stopImpersonation } from "./redux/slices/impersonationSlice";
import { setAuth } from "./redux/slices/authSlice";
import ImpersonateBanner from "./components/ImpersonateBanner";
import { UpcomingDemosProvider } from "./context/UpcomingDemosContext";
import UpcomingDemosModal from "./components/UpcomingDemosModal";

const AnatomyExplorerPage = lazy(() => import("layouts/anatomyExplorer"));

function PublicAnatomyPage() {
  const themeParam =
    typeof window !== "undefined"
      ? String(new URLSearchParams(window.location.search).get("theme") || "").toLowerCase()
      : "";
  const light = themeParam === "light";
  return (
    <Suspense
      fallback={
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: light ? "#f7f2f3" : "#140c0d",
            color: light ? "#8a5b62" : "#8aa4ab",
            display: "grid",
            placeItems: "center",
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: 15,
          }}
        >
          Loading anatomy…
        </div>
      }
    >
      <AnatomyExplorerPage />
    </Suspense>
  );
}

function isFullscreenAnatomyPath(pathname) {
  return (
    pathname === "/anatomy" ||
    pathname === "/anatomy-explorer" ||
    pathname.startsWith("/public/anatomy")
  );
}

function shouldHideSidenav(pathname) {
  const prefixes = [
    "/presentationMakerPage",
    "/presentationMobileView",
    "/public/question",
  ];
  if (prefixes.some((base) => pathname === base || pathname.startsWith(`${base}/`))) {
    return true;
  }
  return isFullscreenAnatomyPath(pathname);
}

function AppPersistGate({ children }) {
  const { pathname } = useLocation();
  if (isFullscreenAnatomyPath(pathname)) return children;
  return (
    <PersistGate loading={null} persistor={persistor}>
      {children}
    </PersistGate>
  );
}

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const [, forceUpdate] = useState({});
  const { pathname } = useLocation();

  // Re-render when impersonation changes (we're outside Provider so subscribe manually)
  useEffect(() => {
    let prevActive = store.getState().impersonation?.active;
    return store.subscribe(() => {
      const active = store.getState().impersonation?.active;
      if (active !== prevActive) {
        prevActive = active;
        forceUpdate({});
      }
    });
  }, []);

  // Sync Redux language to i18n (only en/es have locale files for now)
  useEffect(() => {
    const syncLang = () => {
      const lang = store.getState().general?.lang || "en";
      i18n.changeLanguage(lang === "es" ? "es" : "en");
    };
    syncLang();
    return store.subscribe(syncLang);
  }, []);

  const auth = store.getState().auth || {};
  const impersonation = store.getState().impersonation || {};
  const isAuthenticated = auth?.access_token;
  const role = auth?.role;
  const isImpersonating = impersonation?.active && role === "admin";
  const university = isImpersonating ? impersonation?.university : auth?.universityInfo;
  const effectiveRole = isImpersonating ? "university_admin" : role;
  const routes = getRoutesByRole(effectiveRole);
  const webPermissions = auth?.web_permissions || {};
  const permissionScopedRoutes =
    effectiveRole === "custom_user"
      ? filterRoutesByWebPermissions(routes, webPermissions)
      : routes;

  const filterRoutesByRole = (allRoutes = [], currentRole = "") => {
    const roleKey = String(currentRole || "").toLowerCase();
    return (allRoutes || [])
      .filter((route) => {
        if (!Array.isArray(route?.allowedRoles) || route.allowedRoles.length === 0) {
          return true;
        }
        return route.allowedRoles.map((r) => String(r || "").toLowerCase()).includes(roleKey);
      })
      .map((route) => {
        if (Array.isArray(route?.collapse) && route.collapse.length > 0) {
          const filteredCollapse = filterRoutesByRole(route.collapse, currentRole);
          return { ...route, collapse: filteredCollapse };
        }
        return route;
      })
      .filter((route) => {
        if (Array.isArray(route?.collapse)) {
          return route.collapse.length > 0;
        }
        return true;
      });
  };

  const visibleRoutes = useMemo(
    () => filterRoutesByRole(permissionScopedRoutes, role),
    [permissionScopedRoutes, role]
  );
  const homePath = useMemo(() => {
    if (role === "student") return "/histology";
    if (effectiveRole === "custom_user") {
      return firstAccessibleHomePath(visibleRoutes, "/no-access");
    }
    return "/dashboard";
  }, [effectiveRole, role, visibleRoutes]);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    // Only admin / university_admin should preload the global dashboard stats
    if (role === "admin" || role === "university_admin") {
      GetDashboardReportCall();
      GetMapReportCall();
    }
    document.body.setAttribute("dir", direction);
  }, [direction, role]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return [
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />,
        ];
      }

      return [];
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      sx={{
        cursor: "pointer",
        bgcolor: darkMode ? "rgba(255,255,255,0.08)" : "background.paper",
        color: darkMode ? "#ffffff" : "text.primary",
        border: "1px solid",
        borderColor: darkMode ? "rgba(255,255,255,0.14)" : "divider",
        boxShadow: darkMode ? "0 10px 28px rgba(0,0,0,0.45)" : undefined,
      }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const activeTheme = useMemo(() => {
    const baseTheme = darkMode ? themeDark : theme;
    return getThemeWithAccent(baseTheme, sidenavColor);
  }, [darkMode, sidenavColor]);
  const indexQuestionId =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("question_id")
      : null;

  return (
    <Provider store={store}>
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        <UpcomingDemosProvider>
        {isAuthenticated &&
          layout === "dashboard" &&
          !shouldHideSidenav(pathname) && (
          <>
            <Sidenav
              color={sidenavColor}
              university={university}
              brand={
                (transparentSidenav && !darkMode) || whiteSidenav
                  ? brandDark
                  : brandWhite
              }
              brandName=""
              routes={visibleRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
            {/* Global "Upcoming demos" modal — opened from the sidebar badge. */}
            <UpcomingDemosModal />
          </>
        )}
        {layout === "vr" && <Configurator />}
        {isAuthenticated && isImpersonating && <ImpersonateBanner />}
        <AppPersistGate>
          <Routes>
            {/* Public routes (always available) */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route
              path="/index"
              element={
                indexQuestionId ? (
                  <PublicQuestionPage questionIdOverride={indexQuestionId} />
                ) : (
                  <LandingPage />
                )
              }
            />
            <Route
              path="/presentationMobileView/:id"
              element={<PresentationMobileViewPage />}
            />
            <Route path="/public/question/:id" element={<PublicQuestionPage />} />
            {/* Public marketing form for professors (no auth required). */}
            <Route path="/request-demo" element={<DemoRequestPage />} />
            <Route path="/demo-request" element={<DemoRequestPage />} />
            {/* Same guest access as /authentication/forgot-password. Prefer /public/anatomy
                in production — nginx 403s /anatomy when a static folder exists. */}
            <Route path="/public/anatomy" element={<PublicAnatomyPage />} />
            <Route path="/anatomy" element={<PublicAnatomyPage />} />
            <Route path="/anatomy-explorer" element={<PublicAnatomyPage />} />

            {isAuthenticated ? (
              <>
                {getRoutes(visibleRoutes)}
                {role === "student" ? (
                  <Route
                    path="/quiz"
                    element={<Navigate to="/quiz-by-code" replace />}
                  />
                ) : (
                  <Route
                    path="/quiz"
                    element={<Navigate to="/quiz/titles" replace />}
                  />
                )}
                <Route
                  path="/general-quiz"
                  element={<Navigate to="/general-quiz/titles" replace />}
                />
                <Route
                  path="*"
                  element={<Navigate to={homePath} replace />}
                />
              </>
            ) : (
              <>
                <Route
                  path="/authentication/forgot-password/confirm"
                  element={<ForgotPasswordConfirm />}
                />
                <Route path="/authentication/forgot-password" element={<ForgotPassword />} />
                <Route path="/authentication/sign-in" element={<SignIn />} />
                <Route path="/authentication/sign-up" element={<SignUp />} />
                <Route path="/public/anatomy" element={<PublicAnatomyPage />} />
                <Route path="/anatomy" element={<PublicAnatomyPage />} />
                <Route path="/anatomy-explorer" element={<PublicAnatomyPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        </AppPersistGate>
        </UpcomingDemosProvider>
      </ThemeProvider>
    </Provider>
  );
}
